import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { TableColumn } from 'react-data-table-component';

import customStyles from './CustomTableStyles';
import CustomExpander from './CustomExpander/CustomExpander';

import CustomPagination from 'components/Common/PaginationDataTable';
import Search from 'components/Search/Search';
import { MLSelect } from 'components/Select/Select';

import './FilterDataTable.scss';

const DEFAULT_SEARCH_FIELDS = ['id', 'last_connection', 'serial_number', 'object_name', 'company_name'];

interface FilterDataTableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  title?: string;
  isMinHeight?: boolean;
  isLoading: boolean;
  onChangePage: (page: number) => void;
  onChangeFilterParams: (ordering: string) => void;
  onChangeSearch: (search: string) => void;
  expandableRows?: boolean;
  expandableRowsComponent?: React.FC<{ data: T }>;
  expandableRowsComponentProps?: {
    slug: string;
  };
  iconComponent?: React.ReactNode;
  label?: string;
  searchFields?: string[];
  keyField?: string;
}

function FilterDataTable<T>({
  columns,
  data,
  isMinHeight,
  title,
  onChangeFilterParams,
  onChangeSearch,
  isLoading,
  expandableRows = false,
  expandableRowsComponent,
  expandableRowsComponentProps,
  iconComponent,
  label = 'Devices Per Page',
  searchFields = DEFAULT_SEARCH_FIELDS,
  keyField = 'id',
}: FilterDataTableProps<T>) {
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState<T[]>(data);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    const getNestedValue = (obj: T, path: string) => {
      const keys = path.replace('__', '.').split('.');
      return keys.reduce<unknown>((acc, key) => {
        if (acc && typeof acc === 'object' && key in acc) {
          return (acc as Record<string, unknown>)[key];
        }

        return undefined;
      }, obj);
    };

    const query = searchTerm.trim().toLowerCase();
    let filtered = data.filter(
      item =>
        !query ||
        searchFields.some(column => {
          const value = getNestedValue(item, column);
          return value !== null && value !== undefined && value.toString().toLowerCase().includes(query);
        }),
    );

    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = getNestedValue(a, sortField);
        const bValue = getNestedValue(b, sortField);

        if (aValue === undefined || aValue === null) return 1;
        if (bValue === undefined || bValue === null) return -1;

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }

        const aComparable = String(aValue);
        const bComparable = String(bValue);
        return sortDirection === 'asc'
          ? aComparable.localeCompare(bComparable, undefined, { numeric: true, sensitivity: 'base' })
          : bComparable.localeCompare(aComparable, undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    setFilteredData(filtered);
    setCurrentPage(page => Math.min(page, Math.max(1, Math.ceil(filtered.length / itemsPerPage))));
  }, [searchTerm, data, searchFields, sortField, sortDirection, itemsPerPage]);

  const handleItemsPerPageChange = (value: string | number | null) => {
    const selectedValue = Number(value) || 10;
    setItemsPerPage(selectedValue);
    setCurrentPage(1);
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
    onChangeSearch(search);
    setCurrentPage(1);
  };

  const handleSort = (column: TableColumn<T>, sortDirection: 'asc' | 'desc') => {
    setSortField(column.sortField);
    setSortDirection(sortDirection);
    onChangeFilterParams(sortDirection === 'asc' ? column.sortField || '' : `-${column.sortField}`);
    setCurrentPage(1);
  };

  const paginatedData = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  return (
    <div className="p-3">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <MLSelect
            options={[
              { value: '10', label: '10' },
              { value: '20', label: '20' },
              { value: '50', label: '50' },
              { value: '100', label: '100' },
            ]}
            label={label}
            onChange={handleItemsPerPageChange}
            value={String(itemsPerPage)}
          />
        </div>
        {title && (
          <div className="d-flex align-items-center">
            {iconComponent && <div className="mx-2">{iconComponent}</div>}
            <div className="d-flex align-items-center h4 pt-1 mb-0 fw-bold gap-2 ">{title}</div>
          </div>
        )}
        <div>
          <Search onSearch={handleSearch} />
        </div>
      </div>
      <DataTable
        keyField={keyField}
        columns={columns}
        data={paginatedData}
        progressPending={isLoading}
        pagination={false}
        customStyles={customStyles}
        dense
        onSort={handleSort}
        sortServer
        // Дефолтне поле о напрямок сортування на стороні DataTable
        // defaultSortFieldId={3}
        // defaultSortAsc={false}
        className={isMinHeight ? 'custom-height-dt' : ''}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        expandableIcon={{
          collapsed: <CustomExpander isExpanded={false} />,
          expanded: <CustomExpander isExpanded />,
        }}
        noDataComponent={
          <div className="p-4 text-center text-muted fs-14" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('DeviceWarehouse.DeviceWarehouseTable.deviceTypeEmpty')}
          </div>
        }
      />
      <CustomPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
    </div>
  );
}

export default FilterDataTable;
